import { StorageInterface } from '../storage';
import { HttpClientDecorator } from './HttpClientDecorator';
import { HttpRequestConfig, ResponseHeaders } from './entities';
import { HttpClient } from './interfaces';

export class HttpClientWithPersistentHeaders extends HttpClientDecorator {
  private readonly storageKey = 'headers';
  private persistentHeaders: ResponseHeaders = {};

  constructor(
    httpClient: HttpClient,
    private readonly storageService: StorageInterface,
    private readonly fallbackStorageService?: StorageInterface,
  ) {
    super(httpClient);
    this.loadPersistentHeaders();
  }

  get<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config: HttpRequestConfig = {},
  ): Promise<{ data: ResponseData; meta: ResponseMeta; success: boolean }> {
    return super.get<ResponseData, ResponseMeta>(url, this.addPersistentHeaders(config));
  }

  post<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config: HttpRequestConfig = {},
  ): Promise<{ data: ResponseData; meta: ResponseMeta; success: boolean }> {
    return super.post<ResponseData, ResponseMeta>(url, this.addPersistentHeaders(config));
  }

  put<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config: HttpRequestConfig = {},
  ): Promise<{ data: ResponseData; meta: ResponseMeta; success: boolean }> {
    return super.put<ResponseData, ResponseMeta>(url, this.addPersistentHeaders(config));
  }

  patch<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config: HttpRequestConfig = {},
  ): Promise<{ data: ResponseData; meta: ResponseMeta; success: boolean }> {
    return super.patch<ResponseData, ResponseMeta>(url, this.addPersistentHeaders(config));
  }

  delete<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config: HttpRequestConfig = {},
  ): Promise<{ data: ResponseData; meta: ResponseMeta; success: boolean }> {
    return super.delete<ResponseData, ResponseMeta>(url, this.addPersistentHeaders(config));
  }

  private getPersistentHeaders() {
    return this.persistentHeaders;
  }

  private setPersistentHeaders(headers: ResponseHeaders) {
    this.persistentHeaders = headers;
  }

  async setHeaders(headers: ResponseHeaders) {
    const persistentHeaders = { ...this.getPersistentHeaders(), ...headers };

    this.setPersistentHeaders(persistentHeaders);
    this.savePersistentHeaders();

    return super.setHeaders(headers);
  }

  getHeaders(): ResponseHeaders {
    const headers = { ...super.getHeaders(), ...this.getPersistentHeaders() };

    return headers;
  }

  private addPersistentHeaders(config: HttpRequestConfig): HttpRequestConfig {
    return {
      ...config,
      headers: {
        ...this.getPersistentHeaders(),
        ...config.headers,
      },
    };
  }

  private loadPersistentHeaders() {
    const storedHeaders = this.storageService.get<ResponseHeaders>(this.storageKey);

    if (!!storedHeaders) {
      this.setPersistentHeaders(storedHeaders);
      return;
    }

    if (this.fallbackStorageService) {
      const fallbackHeaders = this.fallbackStorageService.get<ResponseHeaders>(this.storageKey);

      if (!!fallbackHeaders) {
        this.setPersistentHeaders(fallbackHeaders);
      }
    }
  }

  private savePersistentHeaders() {
    this.storageService.set(this.storageKey, this.getPersistentHeaders());
  }
}
